import { format } from "date-fns";
import { MonthKeys, MonthlyUsage } from "queries/billingApi.flickapi";
import { BRAND_COLOURS } from "utils/constants";

const Y_AXIS_DISPLAY_TYPE = {
  cost: "$",
  usage: "kWh",
};

export type MonthlyData = {
  month: string;
  year: number;
  usage: number;
  cost: number;
  spend: number;
  earned: number;
  consumption: number;
  generation: number;
};

/**
 * @description takes a year's worth of daily aggregates data
 * processes it into monthly totals - cost and usage
 * returns the array of monthly totals
 */
export const processAnnualAggregatesData = (
  data: Partial<Record<MonthKeys, MonthlyUsage>>,
  isTaxInclusive: boolean,
): MonthlyData[] => {
  if (!data) return [];

  // Convert the grouped data into an array of months
  const result = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, "0"); // Format month as "01" to "12"

    if (data[month]) {
      return {
        month: month,
        year: data[month].import?.year ?? data[month].export?.year,
        cost: parseFloat(
          parseFloat(data[month].import?.totalInclGst ?? 0).toFixed(2),
        ), // Round total cost to 2 dp
        usage: parseFloat(
          parseFloat(data[month].import?.quantity ?? 0).toFixed(3),
        ), // Round total usage to 3 dp
        spend: parseFloat(
          parseFloat(data[month].import?.totalInclGst ?? 0).toFixed(2),
        ),
        earned: parseFloat(
          Math.abs(
            parseFloat(
              isTaxInclusive
                ? (data[month].export?.totalInclGst ?? 0)
                : (data[month].export?.totalExclGst ?? 0),
            ),
          ).toFixed(2),
        ),
        consumption: parseFloat(
          parseFloat(data[month].import?.quantity ?? 0).toFixed(3),
        ),
        generation: parseFloat(
          Math.abs(parseFloat(data[month].export?.quantity ?? 0)).toFixed(2),
        ),
      };
    }

    // Handle months with no data
    return {
      month,
      year: new Date().getFullYear(),
      usage: 0,
      cost: 0,
      spend: 0,
      earned: 0,
      consumption: 0,
      generation: 0,
    };
  });
  return result;
};

/**
 * @description Takes the processed data for this and prev year, and the displayType.
 * returns the max Y value
 */
export const getMaxYValue = ({ thisYearData, lastYearData, displayType }) => {
  const thisYearTotals =
    thisYearData.length > 0
      ? thisYearData.map((item) => item[displayType])
      : [];

  const lastYearTotals =
    lastYearData.length > 0
      ? lastYearData.map((item) => item[displayType])
      : [];

  return thisYearTotals.length
    ? Math.max(...thisYearTotals, ...lastYearTotals)
    : 0;
};

/**
 * @description Create an array of months in the MMM format.
 * Starts by creating an empty array of length 12 and uses the index to represent each month.
 */
export const createMonthLabels = (): string[] => {
  return Array.from(Array(12), (_, monthIndex) =>
    format(new Date(0, monthIndex, 1), "MMM"),
  );
};

/**
 * @description Create an array of costs or usage (depending on displayType)
 * The chart always assigns the first array value to the month of January and so on.
 * If the first value available for a given year is for a different month, eg March, prepend the
 * array with empty entries, eg 2 empty entries for Jan and Feb.
 */
export const createDataArray = (
  yearData: MonthlyData[],
  displayType: string,
) => {
  if (yearData?.length == 0) return [];

  const firstMonth = yearData[0].month;
  const numberOfPrecedingMonths = Number(firstMonth) - 1;

  // Prepend with empty entries for months before the first data entry
  const result = new Array(numberOfPrecedingMonths).fill({
    [displayType]: "0",
  });

  // Append the actual data
  result.push(...yearData);
  return result.map((data) => data[displayType]);
};

/**
 * @description Generate the configuration for the grouped bar chart
 */

export const generateGroupedBarChartConfig = ({
  lastYearData,
  thisYearData,
  lastYear,
  thisYear,
  displayType = "cost",
}): GroupedBarChartConfig => ({
  labels: createMonthLabels(),
  dates: thisYearData.map((data: MonthlyData) => data.month),
  yAxisText: Y_AXIS_DISPLAY_TYPE[displayType],
  chartTitle: "Monthly usage",
  datasets: [
    {
      label: lastYear,
      data: createDataArray(lastYearData, displayType),
      status: "pending",
      barColour: BRAND_COLOURS.greyShade30,
      hoverBackgroundColour: BRAND_COLOURS.greyShade50,
    },
    {
      label: thisYear,
      data: createDataArray(thisYearData, displayType),
      status: "pending",
      barColour: BRAND_COLOURS.yellow,
      hoverBackgroundColour: BRAND_COLOURS.yellowDark,
    },
  ],
  showZoom: true,
});
